/**
 * DraftCard — a structured draft (quote, ticket, report, work order …): header fields, line items,
 * sections, attachments and totals (CONTRACT §3.4, §10.4).
 *
 * Every spoken or typed edit is one DraftOp; the element it touched is highlighted for 4 s
 * (`highlight` = {kind, target, field, at, removed?}); a removed line stays visible struck through
 * for those 4 s. In edit mode quantities, editable header fields and unlocked sections are
 * editable inline and send set_field / rewrite_section through `onOp(op)`. Locked sections carry
 * domain safety text and are never editable here.
 */

import { useEffect, useState } from 'react';
import { Undo2, Lock, Paperclip, Pencil } from 'lucide-react';
import SyntheticBadge from './SyntheticBadge.jsx';
import FileLink from './FileLink.jsx';
import { formatInr, fileLinkProps } from './agentUi.js';
import './DraftCard.css';

const HIGHLIGHT_MS = 4000;
const EDITABLE_STATUS = new Set(['draft', 'pending_approval']);

const STATUS_LABEL = {
  draft: 'Draft', pending_approval: 'Waiting for approval', approved: 'Approved',
  submitted: 'Submitted', superseded: 'Superseded',
};

/** The highlight entry while it is fresh (4 s from its arrival), else null. */
function useFreshHighlight(highlight) {
  const key = highlight ? `${highlight.kind}|${highlight.target}|${highlight.at}|${highlight.version}` : null;
  const [expired, setExpired] = useState(null);
  useEffect(() => {
    if (!key) return undefined;
    const timer = setTimeout(() => setExpired(key), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [key]);
  return key && key !== expired ? highlight : null;
}

/** Text input that commits on Enter or blur, and reverts on Escape. */
function InlineEdit({ value, onCommit, type = 'text', multiline = false, label, className = '' }) {
  const [draft, setDraft] = useState(String(value ?? ''));
  useEffect(() => { setDraft(String(value ?? '')); }, [value]);
  const commit = () => {
    if (draft === String(value ?? '')) return;
    onCommit(type === 'number' ? Number(draft) : draft);
  };
  const common = {
    className: `dcard__input ${className}`.trim(),
    value: draft,
    'aria-label': label,
    onChange: (e) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown: (e) => {
      if (e.key === 'Escape') { setDraft(String(value ?? '')); e.currentTarget.blur(); }
      if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.blur(); }
    },
  };
  if (multiline) return <textarea rows={3} {...common} />;
  return <input type={type} step={type === 'number' ? 'any' : undefined} min={type === 'number' ? 0 : undefined} {...common} />;
}

export default function DraftCard({
  draft,
  highlight = null,
  onOp,
  editing = false,
  onToggleEdit,
  onFocusLine,
  fileUrl = null,
  compact = false,
}) {
  const hl = useFreshHighlight(highlight);
  if (!draft) return null;

  const money = draft.currency !== null && draft.currency !== undefined;
  const canEdit = EDITABLE_STATUS.has(draft.status) && typeof onOp === 'function';
  const isEditing = editing && canEdit;
  const hlOn = (kind, target) => Boolean(hl && hl.kind === kind && hl.target === target);
  const removed = hl?.kind === 'removed_line' ? hl.removed : null;
  const lines = draft.lines || [];
  const removedIndex = removed ? Math.max(0, Math.min(lines.length, (removed.no ?? lines.length + 1) - 1)) : -1;

  const rows = [];
  lines.forEach((line, i) => {
    if (i === removedIndex) rows.push({ removed: true, line: removed });
    rows.push({ removed: false, line });
  });
  if (removed && removedIndex >= lines.length) rows.push({ removed: true, line: removed });

  return (
    <article
      className={`dcard is-${draft.status}${compact ? ' is-compact' : ''}${isEditing ? ' is-editing' : ''}`}
      aria-label={draft.title}
    >
      <header className="dcard__head">
        <div className="dcard__titles">
          <span className="dcard__kind">{draft.kind.replace('_', ' ')}</span>
          <h3 className="dcard__title">{draft.title}</h3>
        </div>
        <span className="dcard__status">{STATUS_LABEL[draft.status] ?? draft.status}</span>
        <span className="dcard__ver" title="Version">v{draft.version}</span>
      </header>
      <div className="dcard__target">
        To {draft.target?.label || draft.target?.system || 'none'}
        {draft.external?.id && <span className="dcard__ext"> · {draft.external.id}</span>}
      </div>

      {(draft.header || []).length > 0 && (
        <dl className="dcard__header">
          {draft.header.map((f) => (
            <div key={f.key} className={`dcard__hfield${hlOn('field', f.key) ? ' is-highlight' : ''}`}>
              <dt>{f.label}</dt>
              <dd>
                {isEditing && f.editable ? (
                  <InlineEdit
                    value={f.value}
                    label={f.label}
                    type={f.type === 'number' || f.type === 'money' ? 'number' : 'text'}
                    onCommit={(v) => onOp({ op: 'set_field', path: `header.${f.key}`, value: v })}
                  />
                ) : (
                  <span>{f.type === 'money' ? formatInr(f.value) : String(f.value ?? '')}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {rows.length > 0 && (
        <table className="dcard__lines">
          <thead>
            <tr>
              <th className="dcard__no">#</th>
              <th>Item</th>
              <th className="dcard__num">Qty</th>
              {money && <th className="dcard__num">Price</th>}
              {money && <th className="dcard__num">Amount</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ removed: gone, line }) => (
              <tr
                key={`${gone ? 'x' : ''}${line.id}`}
                className={`${gone ? 'is-removed' : ''}${!gone && hlOn('line', line.id) ? ' is-highlight' : ''}`.trim() || undefined}
                onClick={gone ? undefined : () => onFocusLine?.(line.id)}
              >
                <td className="dcard__no">{gone ? '–' : line.no}</td>
                <td>
                  <div className="dcard__desc">{line.description}</div>
                  {line.sku && <div className="dcard__sku">{line.sku}</div>}
                  {line.note && <div className="dcard__note">{line.note}</div>}
                </td>
                <td className={`dcard__num${!gone && hlOn('line', line.id) && hl.field === 'qty' ? ' is-field' : ''}`}>
                  {isEditing && !gone ? (
                    <InlineEdit
                      value={line.qty}
                      type="number"
                      label={`Quantity, line ${line.no}`}
                      className="dcard__qty"
                      onCommit={(v) => { if (Number.isFinite(v) && v > 0) onOp({ op: 'set_field', path: `lines.${line.id}.qty`, value: v }); }}
                    />
                  ) : `${line.qty} ${line.unit}`}
                </td>
                {money && <td className="dcard__num">{formatInr(line.unit_price)}</td>}
                {money && <td className="dcard__num">{formatInr(line.amount)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(draft.sections || []).filter((s) => s.body || isEditing).map((s) => (
        <section key={s.id} className={`dcard__section${hlOn('section', s.id) ? ' is-highlight' : ''}${s.locked ? ' is-locked' : ''}`}>
          <h4 className="dcard__stitle">
            {s.locked && <Lock size={11} aria-label="Locked safety text" />}
            {s.title}
          </h4>
          {isEditing && !s.locked ? (
            <InlineEdit
              value={s.body}
              multiline
              label={s.title}
              onCommit={(v) => onOp({ op: 'rewrite_section', section: s.id, body: v, mode: 'replace' })}
            />
          ) : (
            <p className="dcard__body">{s.body}</p>
          )}
        </section>
      ))}

      {(draft.attachments || []).length > 0 && (
        <ul className="dcard__attachments">
          {draft.attachments.map((a) => (
            <li key={a.id} className={hlOn('attachment', a.id) ? 'is-highlight' : undefined}>
              <Paperclip size={11} aria-hidden="true" />
              {fileUrl ? (
                <FileLink link={fileLinkProps(a.path, fileUrl(a.path))} className={null}>{a.title || a.path}</FileLink>
              ) : (a.title || a.path)}
            </li>
          ))}
        </ul>
      )}

      {money && draft.totals && (
        <dl className="dcard__totals">
          <div><dt>Subtotal</dt><dd>{formatInr(draft.totals.subtotal)}</dd></div>
          <div><dt>Tax {Math.round((draft.totals.tax_rate ?? 0) * 100)}%</dt><dd>{formatInr(draft.totals.tax)}</dd></div>
          <div className="dcard__grand"><dt>Total</dt><dd>{formatInr(draft.totals.total)} {draft.currency}</dd></div>
        </dl>
      )}

      <footer className="dcard__foot">
        <SyntheticBadge />
        {canEdit && (
          <div className="dcard__actions">
            {typeof onToggleEdit === 'function' && (
              <button type="button" className={`dcard__btn${isEditing ? ' is-on' : ''}`} aria-pressed={isEditing} onClick={onToggleEdit}>
                <Pencil size={11} aria-hidden="true" /> {isEditing ? 'Done' : 'Edit'}
              </button>
            )}
            <button type="button" className="dcard__btn" onClick={() => onOp({ op: 'undo' })} title="Undo the last change">
              <Undo2 size={11} aria-hidden="true" /> Undo
            </button>
          </div>
        )}
      </footer>
    </article>
  );
}
